import React, { useEffect, useState } from "react";
import "./worker_weekly.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* ===== FIREBASE ===== */
import { db } from "../firebase";
import { collection, getDocs, getDoc, doc, query, where, limit, documentId } from "firebase/firestore";

/* ===== HELPERS ===== */
const DAYS_MAP = {
  Mo: "monday",
  Di: "tuesday",
  Mi: "wednesday",
  Do: "thursday",
  Fr: "friday",
  Sa: "saturday",
  So: "sunday"
};

const DAY_ORDER = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

const getISOWeek = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  const day = date.getDay() || 7;
  date.setDate(date.getDate() + 4 - day);
  const yearStart = new Date(date.getFullYear(), 0, 1);
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
};

const getDateRangeFromKW = (kw, year) => {
  const simple = new Date(year, 0, 1 + (kw - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = simple;
  if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  const monday = new Date(ISOweekStart);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d) => `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}.${d.getFullYear()}`;
  return `${fmt(monday)} - ${fmt(sunday)}`;
};

const getDayDate = (dayKey, kw, year) => {
  const simple = new Date(year, 0, 1 + (kw - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = simple;
  if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  
  const monday = new Date(ISOweekStart);
  const dayIndex = DAY_ORDER.indexOf(dayKey);
  const targetDate = new Date(monday);
  targetDate.setDate(monday.getDate() + dayIndex);
  
  return `${String(targetDate.getDate()).padStart(2, "0")}.${String(targetDate.getMonth() + 1).padStart(2, "0")}.${targetDate.getFullYear()}`;
};

// Pagalbinė f-ija gauti tikslią datą
const getDayDateObject = (dayKey, kw, year) => {
    const simple = new Date(year, 0, 1 + (kw - 1) * 7);
    const dow = simple.getDay();
    const ISOweekStart = simple;
    if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
    else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
    
    const monday = new Date(ISOweekStart);
    const dayIndex = DAY_ORDER.indexOf(dayKey);
    const targetDate = new Date(monday);
    targetDate.setDate(monday.getDate() + dayIndex);
    return targetDate;
};

const WorkerWeekly = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const currentWorkerId = localStorage.getItem("workerId");
  const [currentWorkerName, setCurrentWorkerName] = useState(localStorage.getItem("workerName") || "");
  const [weekData, setWeekData] = useState([]);
  const [currentKw, setCurrentKw] = useState(getISOWeek()); 
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);

  const changeWeek = (offset) => {
    let newKw = currentKw + offset;
    let newYear = currentYear;
    if (newKw > 52) { newKw = 1; newYear++; } 
    else if (newKw < 1) { newKw = 52; newYear--; }
    setCurrentKw(newKw);
    setCurrentYear(newYear);
  };

  /* 1. AUTENTIKACIJOS PATIKRA */
  useEffect(() => {
    const isRegistered = localStorage.getItem("workerDeviceRegistered") === "true";
    const hasSession = localStorage.getItem("workerGateDone") === "true";

    if (!isRegistered) { navigate("/worker", { replace: true }); return; }
    if (!hasSession) { navigate("/worker/login", { replace: true }); return; }
    if (!currentWorkerId) { navigate("/worker/login", { replace: true }); return; }

    if (currentWorkerId && !currentWorkerName) {
      const fetchName = async () => {
        try {
          const q = query(collection(db, "workers"), where(documentId(), "==", currentWorkerId), limit(1));
          const snap = await getDocs(q);
          if (!snap.empty) {
            const name = snap.docs[0].data().name;
            setCurrentWorkerName(name);
            localStorage.setItem("workerName", name);
          }
        } catch (e) { console.error("Fetch name error:", e); }
      };
      fetchName();
    }
  }, [currentWorkerId, currentWorkerName, navigate]);

  /* 2. DUOMENŲ KROVIMAS */
  useEffect(() => {
    if (!currentWorkerId) return;

    const loadData = async () => {
      setLoading(true);
      setWeekData([]); 
      try {
        const daysDictionary = {};
        const targetKw = Number(currentKw);
        const targetYear = Number(currentYear);

        // --- 1. ATSISIUNČIAME DIENŲ PAKEITIMUS (monthlyWorkHours) ---
        const overrideData = {}; 
        const mondayDate = getDayDateObject("Mo", targetKw, targetYear);
        const sundayDate = getDayDateObject("So", targetKw, targetYear);
        
        const monthsSet = new Set();
        monthsSet.add(`${mondayDate.getFullYear()}_${String(mondayDate.getMonth() + 1).padStart(2, '0')}`);
        monthsSet.add(`${sundayDate.getFullYear()}_${String(sundayDate.getMonth() + 1).padStart(2, '0')}`);

        await Promise.all(Array.from(monthsSet).map(async (ym) => {
            const docId = `${currentWorkerId}_${ym}`;
            try {
                const snap = await getDoc(doc(db, "monthlyWorkHours", docId));
                if (snap.exists()) {
                    overrideData[ym] = snap.data().days || {};
                }
            } catch (e) { console.error(e); }
        }));
        // -----------------------------------------------------------

        // --- 2. NUSKAITOME PROJEKTUS ---
        const qProjects = query(
          collection(db, "projects"), 
          where("kw", "==", targetKw), 
          where("year", "==", targetYear)
        );
        const projectsSnap = await getDocs(qProjects);

        projectsSnap.forEach(docSnap => {
          const data = docSnap.data();
          const projectId = docSnap.id; // SVARBU: GAU NAME PROJEKTO ID
          if (!data?.days) return;

          DAY_ORDER.forEach(dayKey => {
            const workerList = data.days[dayKey];
            if (!Array.isArray(workerList)) return;

            const isAssigned = workerList.some(item => {
              const idInDb = typeof item === 'string' ? item : item.id;
              return idInDb === currentWorkerId;
            });

            if (isAssigned) {
              if (!daysDictionary[dayKey]) {
                daysDictionary[dayKey] = { 
                  dayKey, 
                  dayTranslationKey: DAYS_MAP[dayKey], 
                  dateStr: getDayDate(dayKey, targetKw, targetYear),
                  projects: [] 
                };
              }

              // --- LOGIKA: TIKRINAME AR YRA SPECIFINIS ĮRAŠAS ŠIAI DIENAI IR ŠIAM PROJEKTUI ---
              const thisDate = getDayDateObject(dayKey, targetKw, targetYear);
              const ymKey = `${thisDate.getFullYear()}_${String(thisDate.getMonth() + 1).padStart(2, '0')}`;
              const dNum = String(thisDate.getDate());
              
              // ŠTAI PAKEITIMAS: Ieškome duomenų ne tik pagal dieną, bet ir pagal PROJECT ID
              const dayOverrides = overrideData[ymKey]?.[dNum] || {};
              const projectOverride = dayOverrides[projectId] || {}; // Ieškome konkretaus projekto override

              // Ar yra bendras darbuotojo nustatymas projekte?
              const projectSpecific = data.workerDetails?.[currentWorkerId] || {};

              // Prioritetas: Diena+Projekte (Admin modale) -> Darbuotojas (Projekte) -> Projektas (Bendrai)
              const finalTransport = projectOverride.transport || projectSpecific.transport || data.info?.transport || "-";
              const finalHotel = projectOverride.hotel || projectSpecific.accommodation || data.info?.unterkunft || "-";
              const finalPhone = projectOverride.phone || projectSpecific.phone || data.info?.tel || "-";
              const finalComment = projectOverride.comment || projectSpecific.comment || data.info?.kommentar || "";
              const finalContact = projectOverride.contact || data.info?.kontakt || "-";

              daysDictionary[dayKey].projects.push({
                auftraggeber: data.info?.auftraggeber || "-",
                project: data.info?.bauvorhaben || "-",
                address: data.info?.adresse || "",
                ansprechpartner: data.info?.ansprechpartner || "-",
                contact: finalContact, 
                overnight: finalHotel,
                hotel_phone: finalPhone, 
                transport: finalTransport,
                comment: finalComment,
                team: workerList
                  .filter(item => (typeof item === 'string' ? item !== currentWorkerId : item.id !== currentWorkerId))
                  .map(item => (typeof item === 'string' ? t('worker') : item.name))
                  .join(", ")
              });
            }
          });
        });

        // --- 3. NUSKAITOME LAISVADIENIUS ---
        const qStatus = query(
          collection(db, "weeklyPlans"), 
          where("workerId", "==", currentWorkerId), 
          where("kw", "==", targetKw), 
          where("year", "==", targetYear)
        );
        const statusSnap = await getDocs(qStatus);

        statusSnap.forEach(sDoc => {
          const sData = sDoc.data();
          if (sData.status === "laisva") {
            const dayKey = DAY_ORDER[sData.dayIndex];
            if (!daysDictionary[dayKey]) {
              daysDictionary[dayKey] = {
                dayKey,
                dayTranslationKey: DAYS_MAP[dayKey],
                dateStr: getDayDate(dayKey, targetKw, targetYear),
                projects: [],
                isOff: true
              };
            }
          }
        });

        const result = Object.values(daysDictionary);
        result.sort((a, b) => DAY_ORDER.indexOf(a.dayKey) - DAY_ORDER.indexOf(b.dayKey));
        setWeekData(result);
      } catch (error) { 
        console.error("KLAIDA nuskaitant duomenis:", error); 
      }
      setLoading(false);
    };

    loadData();
  }, [currentWorkerId, currentKw, currentYear, t]);

  return (
    <div className="weekly-page">
      <div className="weekly-header">
          <div className="header-left">
            <button className="gold-back-btn" onClick={() => navigate("/worker/home", { replace: true })}>← {t('back')}</button>
          </div>
          <div className="header-right">
            <div className="worker-name-label">{currentWorkerName}</div>
          </div>
      </div>

      <div className="kw-nav">
          <button className="kw-arrow" onClick={() => changeWeek(-1)}>‹</button>
          <div className="kw-center">
              <div className="kw-title">KW {currentKw}</div>
              <div className="kw-dates">{getDateRangeFromKW(currentKw, currentYear)}</div>
          </div>
          <button className="kw-arrow" onClick={() => changeWeek(1)}>›</button>
      </div>

      <div className="week-scroll">
        {loading && <div className="loading-msg">{t('loading')}...</div>}
        {!loading && weekData.length === 0 && <div className="empty-msg">{t('no_work_this_week')}</div>}

        {weekData.map((d, i) => (
          <div key={i} className="day-group">
            <div className="day-header-main">
                <div className="day-title-main">{t(d.dayTranslationKey)} ({d.dateStr})</div>
            </div>

            {d.isOff && (
              <div className="day-card is-off-card">
                <div className="val highlight-white off-text">
                  🏝️ {t('off_day')}
                </div>
              </div>
            )}

            {d.projects.map((proj, idx) => (
                <div key={idx} className="day-card">
                    {d.projects.length > 1 && <div className="proj-index">{t('project')} #{idx + 1}</div>}
                    <div className="compact-row">
                        <div className="compact-col">
                            <span className="lbl">{t('client')}</span>
                            <span className="val">{proj.auftraggeber}</span>
                        </div>
                        <div className="compact-col">
                            <span className="lbl">{t('project')}</span>
                            <span className="val highlight-white">{proj.project}</span>
                        </div>
                    </div>

                    <div className="compact-full-row">
                          <span className="lbl">{t('address')}</span>
                          <span 
                            className="val highlight-blue" 
                            style={{ cursor: "pointer", textDecoration: "underline" }}
                            onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(proj.address)}`, '_blank')}
                          >
                            📍 {proj.address}
                          </span>
                    </div>

                    <div className="divider-thin"></div>

                    <div className="compact-row">
                        <div className="compact-col">
                            <span className="lbl">{t('contact_person')}</span>
                            <span className="val">{proj.ansprechpartner}</span>
                        </div>
                        <div className="compact-col">
                            <span className="lbl">{t('contact')}</span>
                            <span className="val">{proj.contact}</span>
                        </div>
                    </div>

                    <div className="divider-thin"></div>

                    <div className="compact-row">
                        <div className="compact-col">
                            <span className="lbl">{t('accommodation')}</span>
                            <span 
                                className="val highlight-blue" 
                                style={{ cursor: "pointer", textDecoration: "underline" }}
                                onClick={() => proj.overnight !== "-" && window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(proj.overnight)}`, '_blank')}
                            >
                                🏨 {proj.overnight}
                            </span>
                        </div>
                        <div className="compact-col">
                            <span className="lbl">{t('phone')} ({t('hotel')})</span>
                            <span className="val">{proj.hotel_phone}</span>
                        </div>
                    </div>

                    <div className="divider-thin"></div>

                    <div className="compact-row">
                          <div className="compact-col">
                             <span className="lbl">{t('transport')}</span>
                             <span className="val">{proj.transport}</span>
                          </div>
                    </div>

                    {proj.team && (
                        <div className="compact-full-row">
                            <span className="lbl">{t('employees')}</span>
                            <span className="val small-text">{proj.team}</span>
                        </div>
                    )}

                    {proj.comment && (
                        <div className="comment-box-compact">
                            <span className="lbl-comment">{t('comment')}:</span>
                            <span className="val-comment">{proj.comment}</span>
                        </div>
                    )}
                </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkerWeekly;